// stencil
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Method,
  Watch
} from "@stencil/core";

// external libraries
import { MercuryBundles } from "@genexus/mercury";
import {
  ChEditCustomEvent,
  ComboBoxModel,
  TabularGridColumnSelectorClickedEvent,
  TabularGridColumnSortChangedEvent,
  TabularGridMarkingChangedEvent,
  TabularGridRowContextMenuEvent,
  TabularGridSelectionChangedEvent
} from "@genexus/chameleon-controls-library";

// local
import { Locale } from "../../../../common/locale";
import {
  GxOption,
  ItemsCheckedResult,
  ObjectType
} from "../../../../common/types";
import clearGridSelection from "../common/clear-grid-selection";
import areSetsEqual from "../common/are-sets-equal";
import uncheckIgnoredObjects from "../common/uncheck-ignored";

import type {
  StateForFilter,
  StatusInternal,
  MessageData,
  ModifiedObjectData,
  CommitData,
  CommitCallback,
  ContextMenuCallback,
  LoadLocalChangesCallback,
  PendingItemsCheckedCallback,
  SelectCallback,
  ServerDetails,
  SourceType
} from "../common/types";

import headerDetail, { headerTitle } from "../common/header-renders";
import { changesetToComboBoxModel } from "./utilities/changeset-to-combo-box";
import { foldersToComboBoxModel } from "./utilities/folders-to-combo-box";
import { typesToComboBoxItemModel } from "./utilities/types-to-combo-box";
import renderTabularGridRows, {
  renderTabularGridColumns,
  COLUMNS_IDS
} from "../common/tabular-grid-render";
import sortTabularGridColumn, {
  type SortTabularGridColumnDataType
} from "../../../../common/chameleon/tabular-grid-column-sort";

import { CSS_SELECTORS as CLASSES, ALL_VALUE } from "../common/constants";
import ICONS from "../common/constants";

const FILTER_SECONDARY_CONTAINER_ID = "filters-secondary-container";
const FILTER_SECONDARY_BUTTON_ID = "filter-toggle-button";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tabular-grid",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-team-dev-commit",
  styleUrl: "commit.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-commit"]
})
export class GxIdeTeamDevCommit {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  /**
   * The ComboBoxModel for "Local State" filter.
   */
  #localStatesComboBoxModel: { caption: string; value: StateForFilter }[];

  /**
   * Used to determine if pendingItemsCheckedCallback callback has to be
   * called or not. It should be ignored if the grid checkboxes state is
   * changed programmatically, by setting markRow to false once the object
   * has been successfully updated, for example.
   */
  #rowCheckedChangedByTheUser: boolean = true;

  @Element() el: HTMLGxIdeTeamDevCommitElement;

  /**
   * A reference to the tablar grid. Used to call methods.
   */
  #chGridPendingForUpdateEl!: HTMLChTabularGridElement;

  // sets

  /**
   * A boolean that indicates if all the pending objects, have been committed
   * already.
   */
  @State() allPendingAreCommitted: boolean = false;

  /**
   * A set that holds the id's of the objects that have been committed already.
   */
  @State() alreadyCommittedObjectsIdsSet: Set<string> = new Set<string>([]);
  @Watch("alreadyCommittedObjectsIdsSet")
  alreadyCommittedObjectsIdsSetChanged(newCommittedObjectsIdsSet: Set<string>) {
    const pendingIdsSetAndCommittedIdsSetAreEqual = areSetsEqual(
      newCommittedObjectsIdsSet,
      this.pendingIdsSet
    );

    this.allPendingAreCommitted =
      pendingIdsSetAndCommittedIdsSetAreEqual && this.pendingIdsSet.size > 0;
  }

  /**
   * A set that holds the ids of all the ignored objects. Used to quickly
   * determine if the selected rows belong to "ignored" or not. This
   * information is required for the ContextMenuCallback. This set
   * has to be updated on every loadCallback.
   */
  @State() ignoredIdsSet: Set<string> = new Set<string>([]);

  /**
   * A set that holds the id's of all the pending objects. Used to quickly
   * determine if the selected rows belong to "pending" or "ignored". This
   * information is required for the ContextMenuCallback. This set
   * has to be updated after every loadCallback.
   */
  @State() pendingIdsSet: Set<string> = new Set<string>([]);

  /**
   * A set that stores the marked state of each row. This is
   * required because filtering the grid causes marked cells to lose
   * their marked state.
   */
  @State() markedObjectsSet: Set<string> = new Set<string>([]);
  @Watch("markedObjectsSet")
  markedObjectsSetChanged(newMarkedObjectsSet: Set<string>) {
    this.atLeastOneObjectIsChecked = newMarkedObjectsSet.size > 0;
  }

  // maps

  /**
   * A map that holds the current state of every row ("inserted" |
   * "modified" | "modified-with-conflicts" | "deleted" | "updating" |
   * "update")
   */
  @State() objectsStatusMap: Map<string, StatusInternal> = new Map();
  @Watch("objectsStatusMap")
  objectsStatusMapChanged(newObjectsStatusMap: Map<string, StatusInternal>) {
    const someObjectIsUpdating = [...newObjectsStatusMap.entries()].find(
      ([, state]) => state === "committing"
    );

    if (!someObjectIsUpdating) {
      this.committing = false;
    }
  }

  /**
   * A map that holds object messages, after trying to commit ('info' |
   * 'success' | 'warning' | 'error')
   */
  @State() objectsMessagesDataMap: Map<string, MessageData[]> = new Map();

  // boolean

  /**
   * It holds a boolean indicating if at least one object from the
   * tabular-grid is checked, for merging.
   */
  @State() atLeastOneObjectIsChecked: boolean = false;

  /**
   * A boolean to indicate if the comments input has error or not.
   */
  @State() commentInputHasError: boolean = false;

  /**
   * Set to true when objects are being committed. This helps disabling
   * controls until all objects are done.
   */
  @State() committing: boolean = false;

  /**
   * Hides or shows the filter secondary controls
   */
  @State() filterSecondaryIsHidden = true;

  /**
   * Indicates if any of the filter secondary controls have a condition
   * (have a value other than default "All"). This changes the filter
   * toggle button icon, from "filter" to "filterWithConditions".
   */
  @State() filterSecondaryHasConditions = false;

  /**
   * Used to display a loader. Set to true when loadCallback is called.
   */
  @State() loading: boolean = true;

  // models

  /**
   * changeset options as ComboBoxModel
   */
  @State() changesetsComboBoxModel: ComboBoxModel;

  /**
   * categories options as ComboBoxModel
   */
  // @State() categoriesComboBoxModel: ComboBoxModel;

  /**
   * Pending objects after "action" filter
   */
  @State() pendingObjectsAfterFilter: ModifiedObjectData[] = [];

  /**
   * Ignored objects after "action" filter
   */
  @State() ignoredObjectsAfterFilter: ModifiedObjectData[] = [];

  /**
   * The ComboBoxModel for "Types" filter.
   */
  @State() typesComboBoxModel: ComboBoxModel = [];

  /**
   * All objects ("pending for update" and "ignored objects") before
   * "state" filter
   */
  @State() pendingAndIgnored: CommitData = {
    pendingForCommit: [],
    ignoredObjects: []
  };
  @Watch("pendingAndIgnored")
  pendingAndIgnoredChanged(newPendingAndIgnored: CommitData) {
    // clear previous state
    this.pendingIdsSet.clear(); // before pendingIdsSet.add
    this.ignoredIdsSet.clear(); // before ignoredIdsSet.add
    this.objectsMessagesDataMap = new Map();
    this.alreadyCommittedObjectsIdsSet = new Set();
    this.commentInputHasError = false;
    this.markedObjectsSet = new Set(); // before #markPendingObjects

    newPendingAndIgnored.pendingForCommit?.forEach(pending => {
      this.pendingIdsSet.add(pending.id);
    });
    newPendingAndIgnored.ignoredObjects?.forEach(ignored => {
      this.ignoredIdsSet.add(ignored.id);
    });

    this.#markPendingObjects(); // after pendingIdsSet.add and ignoredIdsSet.add
    this.#setInitialObjectsStatus(); // after pendingIdsSet.add and ignoredIdsSet.add

    this.#applyClientFilter();
  }

  // current values

  /**
   * Holds the current value for the commit "comments"
   */
  @State() commentsValue: string = "";

  /**
   * The current search filter value.
   */
  @State() searchValue: string = "";

  /**
   * Holds the current value for the "type" filter
   */
  @State() selectedType: string;

  /**
   * Holds the current value for the "local state" filter
   */
  @State() selectedState: StateForFilter = ALL_VALUE;

  /**
   * Holds the current value for the "changeset" filter
   */
  @State() selectedChangeset: string;

  /**
   * Holds the current value for the "folder" filter
   */
  @State() selectedFolder: string;

  /**
   * Holds the current value for the "category" filter
   */
  @State() selectedCategory: string;

  /**
   * folder options as ComboBoxModel
   */
  @State() foldersComboBoxModel: ComboBoxModel;

  /**
   * Indicates if the commit button should be disabled
   */
  @State() isCommitDisabled: boolean = false;

  // callbacks

  /**
   * Invoked when user starts the commit process
   */
  @Prop() readonly commitCallback: CommitCallback;

  /**
   * Invoked when user activates the context menu on any of the grids.
   */
  @Prop() readonly objectsContextMenuCallback!: ContextMenuCallback;

  /**
   * Invoked to return the list of local changes
   */
  @Prop() readonly loadCallback!: LoadLocalChangesCallback;

  /**
   * Invoked when user clicks in server url of Update From group.
   */
  @Prop() readonly openServerCallback!: () => Promise<void>;

  /**
   * Invoked to return information of a Font type of data.
   */
  @Prop() readonly pendingItemsCheckedCallback!: PendingItemsCheckedCallback;

  /**
   * Invoked when the user selects rows from the grid
   */
  @Prop() readonly selectCallback: SelectCallback;

  // models

  /**
   * "types" options for the filter.
   */
  @Prop() readonly types: ObjectType[];
  @Watch("types")
  typesChanged(newTypes: ObjectType[]) {
    this.typesComboBoxModel = typesToComboBoxItemModel(newTypes);
  }

  /**
   * "changeset" options for the filter.
   */
  @Prop() readonly changeset: GxOption[];
  @Watch("changeset")
  changesetChanged(newChangesetOptions: GxOption[]) {
    this.changesetsComboBoxModel =
      changesetToComboBoxModel(newChangesetOptions);
  }

  /**
   * "folder" options for the filter.
   */
  @Prop() readonly folders: GxOption[];
  @Watch("folders")
  folderChanged(newFolderOptions: GxOption[]) {
    this.foldersComboBoxModel = foldersToComboBoxModel(newFolderOptions);
  }

  /**
   * "category" options for the filter.
   */
  // @Prop() readonly categories: GxOption[];
  // @Watch("categories")
  // categoryChanged(newCategoryOptions: GxOption[]) {
  //   this.categoriesComboBoxModel =
  //     categoriesToComboBoxModel(newCategoryOptions);
  // }

  // boolean

  /**
   * If true, the "Action" column will display a button instead of a pill,
   * only when the action is
   */
  @Prop() readonly updatePillAsButton: boolean = false;

  /**
   * Information to show in Update From group.
   */
  @Prop() readonly serverDetails: ServerDetails;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    // Initiate "actions" combo box filter
    this.#localStatesComboBoxModel = [
      {
        caption: this.#componentLocale.states.all,
        value: ALL_VALUE
      },
      {
        caption: this.#componentLocale.states.inserted,
        value: "inserted"
      },
      {
        caption: this.#componentLocale.states.modified,
        value: "modified"
      },
      {
        caption: this.#componentLocale.states.conflicted,
        value: "conflicted"
      },
      {
        caption: this.#componentLocale.states.deleted,
        value: "deleted"
      }
    ];

    // intialize combo-box models

    this.typesComboBoxModel = typesToComboBoxItemModel(this.types);
    this.selectedType = this.typesComboBoxModel[0].value;

    this.selectedState = this.#localStatesComboBoxModel[0].value;

    this.changesetChanged(this.changeset);
    this.selectedChangeset =
      this.changesetsComboBoxModel.length > 0
        ? this.changesetsComboBoxModel[0].value
        : ALL_VALUE;

    this.folderChanged(this.folders);
    this.selectedFolder =
      this.foldersComboBoxModel && this.foldersComboBoxModel.length > 0
        ? this.foldersComboBoxModel[0].value
        : ALL_VALUE;

    // this.categoryChanged(this.categories);
    // this.selectedCategory =
    //   this.categoriesComboBoxModel.length > 0
    //     ? this.categoriesComboBoxModel[0].value
    //     : ALL_VALUE;

    this.#evaluateSecondaryFilterHasConditions();
  }

  async componentDidLoad() {
    await this.#loadData();

    // initialize "allPendingAreCommitted"
    this.alreadyCommittedObjectsIdsSetChanged(
      this.alreadyCommittedObjectsIdsSet
    );
  }

  /**
   * Used by the host to instruct the component to force a data reload in the grids.
   */
  @Method()
  async reload() {
    await this.#loadData();
  }

  /**
   * Used by the host to instruct the component to disable the commit button.
   */
  @Method()
  async disableCommit(value: boolean) {
    this.isCommitDisabled = value;
  }

  // commit related

  #commitCheckedClickedHandler = () => {
    this.#commitObjectsHandler([...this.markedObjectsSet]);
  };

  #commitSingleObject: (objectId: string) => () => void =
    objectId => async () => {
      const newobjectsStatusMap = new Map(this.objectsStatusMap);
      newobjectsStatusMap.set(objectId, "committing");
      this.objectsStatusMap = newobjectsStatusMap;
      this.#commitObjectsHandler([objectId]);
    };

  #commitObjectsHandler = async (selection: string[]) => {
    if (this.commentsValue.length === 0) {
      // A comment is required to commit.
      this.commentInputHasError = true;
      return;
    }
    this.commentInputHasError = false;
    this.committing = true;

    selection.forEach(itemToUpdate => {
      this.objectsStatusMap.set(itemToUpdate, "committing");
    });

    const commitResult = await this.commitCallback(
      selection,
      this.commentsValue
    );

    commitResult.forEach(async objectResult => {
      const messages = objectResult.messages;
      if (messages.length) {
        this.objectsMessagesDataMap.set(objectResult.id, objectResult.messages);
      } else {
        // clear messages
        this.objectsMessagesDataMap.delete(objectResult.id);
      }
      if (objectResult.result === "committed") {
        // Apply a new Set to "alreadyCommittedObjectsIdsSet" in order for the Watch to fire.
        this.alreadyCommittedObjectsIdsSet = new Set([
          ...this.alreadyCommittedObjectsIdsSet,
          objectResult.id
        ]);
        this.objectsStatusMap.set(objectResult.id, "committed");
        await this.#chGridPendingForUpdateEl.markRow(objectResult.id, false);
      } else {
        this.objectsStatusMap.set(objectResult.id, "pending");
      }
    });

    this.committing = false;
    this.commentsValue = "";
  };

  // handlers

  #rowMarkingChangedHandler = async (
    event: CustomEvent<TabularGridMarkingChangedEvent>
  ) => {
    const markedRowsIds = event.detail.rowsId;

    this.markedObjectsSet = new Set(
      markedRowsIds.filter(
        markedObjectId =>
          !this.alreadyCommittedObjectsIdsSet.has(markedObjectId)
      )
    );

    if (this.#rowCheckedChangedByTheUser) {
      this.#updatePendingCheckedItems(event);
    }
  };

  #rowSelectionChangedHandler = async (
    event: CustomEvent<TabularGridSelectionChangedEvent>
  ) => {
    const selectedRowsIds = event.detail.rowsId;
    const firstSelectedRowId = selectedRowsIds[0];
    const gridSource: SourceType = this.pendingIdsSet.has(firstSelectedRowId)
      ? "pending"
      : "ignored";

    const filteredSelectedRowsIds = clearGridSelection(
      gridSource,
      selectedRowsIds,
      this.pendingIdsSet,
      this.ignoredIdsSet,
      this.#chGridPendingForUpdateEl
    );

    await this.selectCallback(gridSource, filteredSelectedRowsIds);
  };

  #commentsInputHandler = (event: ChEditCustomEvent<string> | InputEvent) => {
    this.commentsValue = event.detail as string;
    if (this.commentsValue.length && this.commentInputHasError) {
      // remove error style and tooltip
      this.commentInputHasError = false;
    }
  };

  // filter related

  #applyClientFilter = () => {
    this.pendingObjectsAfterFilter = this.#filteredObjects(
      this.pendingAndIgnored.pendingForCommit
    );
    this.ignoredObjectsAfterFilter = this.#filteredObjects(
      this.pendingAndIgnored.ignoredObjects
    );
  };

  #filteredObjects = (objects: ModifiedObjectData[]): ModifiedObjectData[] => {
    let filtered = objects;

    // name or description
    if (this.searchValue.length) {
      filtered = filtered.filter(object => {
        const nameFiltered = object.name
          .toLowerCase()
          .includes(this.searchValue.toLowerCase());

        const descriptionFiltered = object.description
          .toLowerCase()
          .includes(this.searchValue.toLowerCase());

        return nameFiltered || descriptionFiltered;
      });
    }

    // status
    if (this.selectedState && this.selectedState !== ALL_VALUE) {
      filtered = filtered.filter(object => object.state === this.selectedState);
    }

    return filtered;
  };

  #evaluateSecondaryFilterHasConditions = () => {
    if (
      // this.selectedChangeset !== ALL_VALUE ||
      this.selectedFolder !== ALL_VALUE
      // this.selectedCategory !== ALL_VALUE
    ) {
      this.filterSecondaryHasConditions = true;
    } else {
      this.filterSecondaryHasConditions = false;
    }
  };

  #toggleFiltersHandler = () => {
    this.filterSecondaryIsHidden = !this.filterSecondaryIsHidden;
  };

  #searchInputHandler = (event: ChEditCustomEvent<string> | InputEvent) => {
    this.searchValue = (event.detail as string).trim();
    this.#applyClientFilter();
  };

  #typeChangedHandler = (event: CustomEvent<string> | InputEvent) => {
    this.selectedType = event.detail as string;
    this.#loadData();
  };

  #stateChangedHandler = (event: CustomEvent<string> | InputEvent) => {
    this.selectedState = event.detail as StateForFilter;
    this.#applyClientFilter();
  };

  // changeset is a WIP.
  // #changesetChangedHandler = (event: CustomEvent<string> | InputEvent) => {
  //   this.selectedChangeset = event.detail as string;
  //   this.#loadData();
  //   this.#evaluateSecondaryFilterHasConditions();
  // };

  #folderChanged = (event: CustomEvent<string> | InputEvent) => {
    this.selectedFolder = event.detail as string;
    this.#loadData();
    this.#evaluateSecondaryFilterHasConditions();
  };

  // #categoryChangedHandler = (event: CustomEvent<string> | InputEvent) => {
  //   this.selectedCategory = event.detail as string;
  //   this.#loadData();
  //   this.#evaluateSecondaryFilterHasConditions();
  // };

  #onRowContextMenuHandler =
    () => async (ev: CustomEvent<TabularGridRowContextMenuEvent>) => {
      ev.preventDefault();
      ev.stopPropagation();

      const selectedRowsIds =
        await this.#chGridPendingForUpdateEl.getSelectedRows();

      const firstSelectedRowId = selectedRowsIds[0];
      const gridSource = this.pendingIdsSet.has(firstSelectedRowId)
        ? "pending"
        : "ignored";

      await this.objectsContextMenuCallback(gridSource, {
        selection: selectedRowsIds,
        clientX: ev.detail.clientX,
        clientY: ev.detail.clientY
      });
    };

  // other methods

  #updatePendingCheckedItems = async (
    event: CustomEvent<TabularGridMarkingChangedEvent>
  ) => {
    this.#rowCheckedChangedByTheUser = false;
    const addedRowsIds = event.detail.addedRowsId;
    const removedRowsId = event.detail.removedRowsId;

    try {
      const pendingItemCheckedResult = await this.pendingItemsCheckedCallback({
        itemsChecked: addedRowsIds,
        itemsUnchecked: removedRowsId
      });

      if (pendingItemCheckedResult === undefined) {
        // undo check/uncheck
        await this.#revertPendingCommitsCheckboxes(addedRowsIds, removedRowsId);
      } else {
        await this.#togglePendingCommitsCheckboxes(pendingItemCheckedResult);
      }
    } catch {
      //catch exception if necessary
    } finally {
      this.#rowCheckedChangedByTheUser = true;
    }
  };

  #setInitialObjectsStatus = (): void => {
    this.pendingAndIgnored.pendingForCommit.forEach(pendingObject => {
      const status: StatusInternal = "pending";
      this.objectsStatusMap.set(pendingObject.id, status);
    });
    this.pendingAndIgnored.ignoredObjects.forEach(ignoredObject => {
      const status: StatusInternal = "ignored";
      this.objectsStatusMap.set(ignoredObject.id, status);
    });
  };

  #loadData = async (): Promise<void> => {
    this.loading = true;

    this.pendingAndIgnored = await this.loadCallback({
      search: this.searchValue,
      type: this.selectedType,
      changeset: this.selectedChangeset,
      folder: this.selectedFolder,
      category: this.selectedCategory
    });

    this.loading = false;
    // Is is required to mark/check all objects after loadCallback.
    // this.#markPendingObjects();
  };

  /**
   * Marks all pending objects that have not been committed yet.
   *
   * Iterates through all pending object IDs and adds them to the marked objects set
   * if they are not already in the committed objects set. This effectively identifies
   * and marks all the pending objects that still need to be processed.
   **/
  #markPendingObjects = () => {
    for (const pendingId of this.pendingIdsSet) {
      if (!this.alreadyCommittedObjectsIdsSet.has(pendingId)) {
        this.markedObjectsSet = new Set([...this.markedObjectsSet, pendingId]);
      }
    }
  };

  // render methods

  #revertPendingCommitsCheckboxes = async (
    addedRowsIds: string[],
    removedRowsId: string[]
  ) => {
    if (this.#chGridPendingForUpdateEl) {
      for (const rowId of addedRowsIds) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, false);
      }
      for (const rowId of removedRowsId) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, true);
      }
    }
  };

  #togglePendingCommitsCheckboxes = async (
    itemsToToggle: ItemsCheckedResult
  ) => {
    if (this.#chGridPendingForUpdateEl) {
      const itemsToCheck: string[] = itemsToToggle.itemsToCheck;
      const itemsToUncheck: string[] = itemsToToggle.itemsToUncheck;
      // check
      for (const rowId of itemsToCheck) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, true);
      }
      // uncheck
      for (const rowId of itemsToUncheck) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, false);
      }
    }
  };

  #sortTabularGridColumnCallback = (
    e: CustomEvent<TabularGridColumnSortChangedEvent>
  ) => {
    const columnId = e.detail.columnId;

    let objectProperty;
    let dateType: SortTabularGridColumnDataType = "string";
    if (columnId === COLUMNS_IDS.type) {
      objectProperty = "name"; // the type name
      dateType = "object";
    } else if (
      columnId === COLUMNS_IDS.modifiedOn ||
      columnId === COLUMNS_IDS.lastSynchronized
    ) {
      dateType = "dateString";
    }
    this.pendingObjectsAfterFilter = [
      ...sortTabularGridColumn(
        e,
        this.pendingObjectsAfterFilter,
        dateType,
        objectProperty
      )
    ];
    this.ignoredObjectsAfterFilter = [
      ...sortTabularGridColumn(
        e,
        this.ignoredObjectsAfterFilter,
        dateType,
        objectProperty
      )
    ];
  };

  /**
   * WA: Ignored objects must always remain unchecked.
   * Because `tabular-grid` selects all rows when the checkbox column is toggled,
   * we need to manually unselect the ignored IDs. // TODO: Move this function
   * to common
   */
  #columnSelectorClickedCallback = async (
    e: CustomEvent<TabularGridColumnSelectorClickedEvent>
  ) => {
    const allRowsChecked = e.detail;
    if (allRowsChecked) {
      const markedRowsIds =
        await this.#chGridPendingForUpdateEl.getMarkedRows();
      const ignoredMarkedRowsIds = markedRowsIds.filter(rowId => {
        return this.ignoredIdsSet.has(rowId);
      });
      if (ignoredMarkedRowsIds.length) {
        uncheckIgnoredObjects(
          ignoredMarkedRowsIds,
          this.#chGridPendingForUpdateEl
        );
      }
    }
  };

  render() {
    const disableControls =
      this.committing || this.loading || this.isCommitDisabled;
    const gridIsEmpty =
      this.pendingObjectsAfterFilter.length === 0 &&
      this.ignoredObjectsAfterFilter.length === 0;

    const filterIcon = this.filterSecondaryHasConditions
      ? ICONS.filterWithConditions
      : ICONS.filter;

    const commitButtonEnabled =
      this.atLeastOneObjectIsChecked &&
      this.commentsValue.length > 0 &&
      !this.allPendingAreCommitted &&
      !this.isCommitDisabled;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          <header class={{ [CLASSES.HEADER]: true }}>
            <div
              // header details
              class={{ [CLASSES.HEADER_DETAILS]: true }}
            >
              {headerTitle(this.#componentLocale.commitDetail.title)}

              {
                // genexus server
                headerDetail(
                  "link",
                  this.#componentLocale.commitDetail.genexusServer,
                  this.serverDetails ? this.serverDetails.serverUrl : "",
                  this.openServerCallback,
                  "detail__genexus-server"
                )
              }

              {
                // knowledge base
                headerDetail(
                  "text",
                  this.#componentLocale.commitDetail.knowledgeBase,
                  this.serverDetails ? this.serverDetails.kbName : "",
                  undefined,
                  "detail__knowledge-base"
                )
              }

              {
                // version
                headerDetail(
                  "text",
                  this.#componentLocale.commitDetail.version,
                  this.serverDetails ? this.serverDetails.versionName : "",
                  undefined,
                  "detail__version"
                )
              }
            </div>
          </header>

          <div class="filter-container spacing-body-inline">
            <div class={{ [CLASSES.FILTER]: true, "filter-primary": true }}>
              <ch-edit
                // searchch-edit
                class="input filter__combo"
                disabled={disableControls}
                startImgSrc={ICONS.search}
                accessibleName={
                  this.#componentLocale.filter.searchAccessibleName
                }
                placeholder={this.#componentLocale.filter.searchPlaceholder}
                type="search"
                onInput={this.#searchInputHandler}
              ></ch-edit>

              <ch-combo-box-render
                // types
                class="combo-box filter__combo"
                disabled={disableControls}
                model={this.typesComboBoxModel}
                value={this.selectedType}
                accessibleName={this.#componentLocale.filter.typeAccesibleName}
                placeholder={this.#componentLocale.filter.typePlaceholder}
                onInput={this.#typeChangedHandler}
              ></ch-combo-box-render>

              <ch-combo-box-render
                // local states
                class="combo-box filter__combo"
                disabled={disableControls}
                model={this.#localStatesComboBoxModel}
                value={this.selectedState}
                onInput={this.#stateChangedHandler}
                accessibleName={
                  this.#componentLocale.filter.localStateAccesibleName
                }
                placeholder={this.#componentLocale.filter.localStatePlaceholder}
              ></ch-combo-box-render>
            </div>

            <button
              // toggle secondary filter button
              id={FILTER_SECONDARY_BUTTON_ID}
              class="button-secondary button-icon-only"
              disabled={disableControls}
              aria-label={this.#componentLocale.filter.secondaryFilterAriaLabel}
              aria-expanded={this.filterSecondaryIsHidden ? "false" : "true"}
              aria-controls={FILTER_SECONDARY_CONTAINER_ID}
              onClick={this.#toggleFiltersHandler}
              title={this.#componentLocale.filter.secondaryFilterAriaLabel}
            >
              <ch-image class="icon-m" src={filterIcon}></ch-image>
            </button>

            <div class="buttons-spacer">
              <button
                // reload button
                class="button-secondary button-icon-and-text"
                disabled={disableControls}
                onClick={this.#loadData}
              >
                <ch-image class="icon-m" src={ICONS.refresh}></ch-image>
                {this.#componentLocale.filter.reload}
              </button>

              <button
                // commit button
                class="button-primary"
                disabled={!commitButtonEnabled || disableControls}
                onClick={this.#commitCheckedClickedHandler}
              >
                {this.#componentLocale.filter.commit}
              </button>
            </div>

            <div
              // secondary filter
              id={FILTER_SECONDARY_CONTAINER_ID}
              aria-labelledby={FILTER_SECONDARY_BUTTON_ID}
              class={{
                [CLASSES.FILTER]: true,
                "filter-secondary": true,
                "filter-secondary--visible": !this.filterSecondaryIsHidden
              }}
              inert={this.filterSecondaryIsHidden}
              hidden={this.filterSecondaryIsHidden}
            >
              <div>
                {/* 
                  // changeset is a WIP.
                  <ch-combo-box-render
                  // changeset
                  class="combo-box"
                  disabled={disableControls}
                  model={this.changesetsComboBoxModel}
                  value={this.selectedChangeset}
                  onInput={this.#changesetChangedHandler}
                  accessibleName={
                    this.#componentLocale.filter.changesetAccesibleName
                  }
                  placeholder={
                    this.#componentLocale.filter.changesetPlaceholder
                  }
                ></ch-combo-box-render> */}

                <ch-combo-box-render
                  // folder
                  class="combo-box"
                  disabled={disableControls}
                  model={this.foldersComboBoxModel}
                  value={this.selectedFolder}
                  onInput={this.#folderChanged}
                  accessibleName={
                    this.#componentLocale.filter.folderAccesibleName
                  }
                  placeholder={this.#componentLocale.filter.folderPlaceholder}
                ></ch-combo-box-render>

                {/* <ch-combo-box-render
                  // category
                  class="combo-box"
                  disabled={disableControls}
                  model={this.categoriesComboBoxModel}
                  value={this.selectedCategory}
                  onInput={this.#categoryChangedHandler}
                  accessibleName={
                    this.#componentLocale.filter.categoryPlaceholder
                  }
                  placeholder={this.#componentLocale.filter.categoryPlaceholder}
                ></ch-combo-box-render> */}
              </div>
            </div>

            <ch-edit
              // commit comments
              class="input comments"
              multiline={true}
              autoGrow={true}
              placeholder={this.#componentLocale.commitCommentPlaceholder}
              onInput={this.#commentsInputHandler}
              value={this.commentsValue}
            ></ch-edit>
          </div>

          <div class={CLASSES.MAIN}>
            {!this.loading ? (
              <ch-tabular-grid
                class={{
                  "tabular-grid": true,
                  "objects-tabular-grid": true,
                  "disabled-custom-selector": this.committing,
                  "empty-result": gridIsEmpty
                }}
                keyboardNavigationMode="focus"
                rowSelectionMode="multiple"
                onSelectionChanged={this.#rowSelectionChangedHandler}
                onRowMarkingChanged={this.#rowMarkingChangedHandler}
                onRowContextMenu={this.#onRowContextMenuHandler()}
                ref={(el: HTMLChTabularGridElement) =>
                  (this.#chGridPendingForUpdateEl = el)
                }
              >
                {renderTabularGridColumns(
                  ["action"],
                  gridIsEmpty,
                  this.#sortTabularGridColumnCallback,
                  this.#columnSelectorClickedCallback,
                  this.#componentLocale,
                  !this.allPendingAreCommitted
                )}

                {!gridIsEmpty
                  ? [
                      <ch-tabular-grid-rowset class="tabular-grid-rowset">
                        <ch-tabular-grid-rowset-legend class="tabular-grid-rowset-legend">
                          {this.#componentLocale.objects.pendingCommits}
                        </ch-tabular-grid-rowset-legend>
                        {renderTabularGridRows(
                          "pending",
                          this.pendingObjectsAfterFilter,
                          this.ignoredObjectsAfterFilter,
                          this.alreadyCommittedObjectsIdsSet,
                          this.markedObjectsSet,
                          this.objectsMessagesDataMap,
                          this.updatePillAsButton,
                          this.#commitSingleObject,
                          this.objectsStatusMap,
                          this.searchValue,
                          this.#componentLocale
                        )}
                      </ch-tabular-grid-rowset>,

                      <ch-tabular-grid-rowset class="tabular-grid-rowset">
                        <ch-tabular-grid-rowset-legend class="tabular-grid-rowset-legend">
                          {this.#componentLocale.objects.ignoredObjects}
                        </ch-tabular-grid-rowset-legend>
                        {renderTabularGridRows(
                          "ignored",
                          this.pendingObjectsAfterFilter,
                          this.ignoredObjectsAfterFilter,
                          this.alreadyCommittedObjectsIdsSet,
                          this.markedObjectsSet,
                          this.objectsMessagesDataMap,
                          this.updatePillAsButton,
                          this.#commitSingleObject,
                          this.objectsStatusMap,
                          this.searchValue,
                          this.#componentLocale
                        )}
                      </ch-tabular-grid-rowset>
                    ]
                  : null}

                <ch-tabular-grid-rowset class="tabular-grid-rowset">
                  {gridIsEmpty && (
                    <ch-tabular-grid-rowset-empty>
                      <gx-ide-empty-state
                        class="empty-state"
                        isAnimated
                        stateIconSrc={ICONS.filter}
                        stateTitle="No object matched your filter"
                      ></gx-ide-empty-state>
                    </ch-tabular-grid-rowset-empty>
                  )}
                </ch-tabular-grid-rowset>
              </ch-tabular-grid>
            ) : (
              <gx-ide-loader
                cancelLabel={this.#componentLocale.loader.cancelLabel}
                loaderTitle={this.#componentLocale.loader.title}
                description={this.#componentLocale.loader.description}
                show
                class="loader"
              ></gx-ide-loader>
            )}
          </div>
        </section>
      </Host>
    );
  }
}
